import { useOutsideClick } from '@/hooks';
import { MouseEvent, useCallback, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { DropdownInput } from '../DropdownInput';

type DropdownKey = string | number;

export type DropdownOption<TValue extends DropdownKey> = {
  label: string;
  value: TValue;
  disabled?: boolean;
};

type DropdownArrowProps<TValue extends DropdownKey> = {
  placeholder?: string;
  selectedValue?: TValue;
  options: DropdownOption<TValue>[];
  onSelect?: (value?: TValue) => void;
  className?: string;
  withArrow?: boolean;
  disabled?: boolean;
  label?: string;
  inputLabelClassName?: string;
  onRemoveAllSelectedClick?: () => void;
};

export const Dropdown = <TValue extends DropdownKey>({
  selectedValue,
  options,
  placeholder,
  onSelect,
  className,
  disabled,
  withArrow = true,
  label,
  inputLabelClassName,
  onRemoveAllSelectedClick,
}: DropdownArrowProps<TValue>) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  const onOutsideClick = useCallback(() => {
    setIsOpen(false);
  }, []);

  useOutsideClick(ref, onOutsideClick, isOpen);

  const handleOnSelect = useCallback(
    (event: MouseEvent, value: TValue) => {
      event.stopPropagation();
      setIsOpen(false);
      onSelect?.(value);
    },
    [onSelect],
  );

  const selectedLabel = useMemo(
    () => options.find((option) => option.value === selectedValue)?.label,
    [options, selectedValue],
  );

  return (
    <DropdownInput
      placeholder={placeholder}
      inputValue={selectedLabel}
      buttonRef={ref}
      className={className}
      withArrow={withArrow}
      disabled={disabled}
      onClick={() => setIsOpen((prev) => !prev)}
      isOpen={isOpen}
      label={label}
      inputLabelClassName={inputLabelClassName}
      onRemoveAllSelectedClick={onRemoveAllSelectedClick}
    >
      <ul className="absolute z-10 flex max-h-44 w-full flex-1 flex-col gap-0.5 divide-y overflow-auto bg-white shadow-custom">
        {options.map((option) => (
          <li
            key={option.value}
            className={twMerge(
              'p-2.5 text-left text-gray-400 transition-colors',
              'hover:bg-gray-100',
              option.disabled && 'text-gray-200',
              option.value === selectedValue && 'text-gray',
            )}
            onClick={(event) => {
              event.stopPropagation();
              handleOnSelect(event, option.value);
            }}
          >
            {option.label}
          </li>
        ))}
      </ul>
    </DropdownInput>
  );
};
